<script setup lang="ts">
import { reactive, ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useHead } from '@unhead/vue'
import { DEFAULT_LOCALE, type Locale } from '@/i18n'
import { staticPageHref } from '@/router/products'
import { useAuth } from '@/composables/useAuth'
import AccountLayout from '@/components/layout/AccountLayout.vue'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { member } = useAuth()

const currentLocale = computed<Locale>(() => (route.meta.locale as Locale) ?? DEFAULT_LOCALE)
const loginHref = computed(() => staticPageHref('login', currentLocale.value))
useHead({ title: computed(() => `${t('auth.account.contact.title')} — MySmerra`) })

/** Accès réservé. */
onMounted(() => {
  if (!member.value) router.replace(loginHref.value)
})

/**
 * Gare de triage Zendesk : on qualifie la demande en amont avec uniquement le
 * motif et sa précision, puis le formulaire GET (action = `requests/new`) ouvre
 * la page Zendesk pré-remplie. Chaque champ porte `name="tf_<id>"`. Les marqueurs
 * de liste sont fournis en clair (les accolades `{{ }}` Zendesk, non URL-safe,
 * sont remplacées par `__`).
 */
const ZENDESK_ACTION = 'https://aide.app.smerra.fr/hc/fr/requests/new'
const TICKET_FORM_ID = '25320470115730'
const TF = {
  reason: 'tf_25264860778642',
  subAdhesion: 'tf_25319087273618',
  subReclamation: 'tf_25319289703826',
  subContract: 'tf_25319961093394',
  subContractHealth: 'tf_25319501379346',
  subContractAbroad: 'tf_25319825248914',
}

/** `value` = marqueur envoyé à Zendesk (inchangé) ; `key` = libellé i18n. */
type Opt = { value: string; key: string }
const REASONS: Opt[] = [
  { value: '__dc.probleme_de_compte__', key: 'probleme_de_compte' },
  { value: '__dc.suppression_de_donnees__', key: 'suppression_de_donnees' },
  { value: '__dc.teletransmission__', key: 'teletransmission' },
  { value: '__dc.adhesion__', key: 'adhesion' },
  { value: '__dc.reclamation__', key: 'reclamation' },
  { value: '__dc.question_sur_mon_contrat__', key: 'question_contrat' },
]
const SUB_ADHESION: Opt[] = [
  { value: '__dc.choix_produit__', key: 'choix_produit' },
  { value: '__dc.probleme_de_paiement__', key: 'probleme_paiement' },
  { value: '__dc.autre__', key: 'autre' },
]
const SUB_RECLAMATION: Opt[] = [
  { value: '__dc.resiliation__', key: 'resiliation' },
  { value: '__dc.remboursement__', key: 'remboursement' },
  { value: 'dc.autre__', key: 'autre' },
]
const SUB_CONTRACT: Opt[] = [
  { value: 'complementaire_sante', key: 'complementaire_sante' },
  { value: 'studlife-ass-sante', key: 'studlife' },
  { value: 'assurance_habitation', key: 'assurance_habitation' },
  { value: 'assurance_vie_etudiante', key: 'assurance_vie_etudiante' },
  { value: 'assurance_a_l_etranger', key: 'assurance_etranger' },
  { value: 'autre_contrat_assurance_clone', key: 'autre' },
]
const SUB_CONTRACT_HEALTH: Opt[] = [
  { value: '__dc.remboursement_de_soins__', key: 'remboursement_soins' },
  { value: '__dc.probleme_de_paiement_de_cotisation__', key: 'probleme_paiement_cotisation' },
  { value: 'autre_contrat_clone', key: 'autre' },
]
const SUB_CONTRACT_ABROAD: Opt[] = [
  { value: '__dc.demande_de_waivers__', key: 'demande_waivers' },
  { value: 'autre_assu_etranger', key: 'autre' },
]

const form = reactive({
  reason: '',
  subAdhesion: '',
  subReclamation: '',
  subContract: '',
  subContractHealth: '',
  subContractAbroad: '',
})

const showAdhesion = computed(() => form.reason === '__dc.adhesion__')
const showReclamation = computed(() => form.reason === '__dc.reclamation__')
const showContract = computed(() => form.reason === '__dc.question_sur_mon_contrat__')
const showContractHealth = computed(() => showContract.value && form.subContract === 'complementaire_sante')
const showContractAbroad = computed(() => showContract.value && form.subContract === 'assurance_a_l_etranger')

/* --- Validation : seuls le motif et sa précision visible sont requis. --- */
const submitted = ref(false)

const errors = computed<Record<string, string>>(() => {
  const e: Record<string, string> = {}
  if (!form.reason) e.reason = t('auth.account.contact.errors.required')
  if (showAdhesion.value && !form.subAdhesion) e.subAdhesion = t('auth.account.contact.errors.required')
  if (showReclamation.value && !form.subReclamation) e.subReclamation = t('auth.account.contact.errors.required')
  if (showContract.value && !form.subContract) e.subContract = t('auth.account.contact.errors.required')
  if (showContractHealth.value && !form.subContractHealth) e.subContractHealth = t('auth.account.contact.errors.required')
  if (showContractAbroad.value && !form.subContractAbroad) e.subContractAbroad = t('auth.account.contact.errors.required')
  return e
})

/** Au submit : si des erreurs, on bloque l'envoi natif et on les affiche.
 *  Sinon, le formulaire GET ouvre la page Zendesk pré-remplie. */
function onSubmit(ev: Event) {
  submitted.value = true
  if (Object.keys(errors.value).length > 0) ev.preventDefault()
}
const showErr = (key: string) => submitted.value && !!errors.value[key]
</script>

<template>
  <AccountLayout active="contact">
    <section class="contact">
      <header class="contact__head">
        <span class="contact__head-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="22" height="22">
            <rect x="3" y="5" width="18" height="14" rx="2.5" fill="none" stroke="currentColor" stroke-width="1.7"/>
            <path d="M4 7l8 6 8-6" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
        <div>
          <h1 class="contact__title">{{ t('auth.account.contact.title') }}</h1>
          <p class="contact__intro">{{ t('auth.account.contact.intro') }}</p>
        </div>
      </header>

      <!-- Formulaire GET : ouvre la page Zendesk pré-remplie (nouvel onglet). -->
      <form
        class="contact__form"
        :action="ZENDESK_ACTION"
        method="get"
        target="_blank"
        rel="noopener"
        novalidate
        @submit="onSubmit"
      >
        <input type="hidden" name="ticket_form_id" :value="TICKET_FORM_ID" />

        <div class="contact__field contact__field--full">
          <label class="contact__label" for="c-reason">{{ t('auth.account.contact.reason') }} <span class="contact__req">*</span></label>
          <select id="c-reason" class="contact__input" :class="{ 'is-invalid': showErr('reason') }" :name="TF.reason" v-model="form.reason" :aria-invalid="showErr('reason')">
            <option value="" disabled>{{ t('auth.account.contact.choose') }}</option>
            <option v-for="o in REASONS" :key="o.value" :value="o.value">{{ t('auth.account.contact.options.' + o.key) }}</option>
          </select>
          <p v-if="showErr('reason')" class="contact__error">{{ errors.reason }}</p>
        </div>

        <div v-if="showAdhesion" class="contact__field contact__field--full">
          <label class="contact__label" for="c-adhesion">{{ t('auth.account.contact.detail') }} <span class="contact__req">*</span></label>
          <select id="c-adhesion" class="contact__input" :class="{ 'is-invalid': showErr('subAdhesion') }" :name="TF.subAdhesion" v-model="form.subAdhesion" :aria-invalid="showErr('subAdhesion')">
            <option value="" disabled>{{ t('auth.account.contact.choose') }}</option>
            <option v-for="o in SUB_ADHESION" :key="o.value" :value="o.value">{{ t('auth.account.contact.options.' + o.key) }}</option>
          </select>
          <p v-if="showErr('subAdhesion')" class="contact__error">{{ errors.subAdhesion }}</p>
        </div>

        <div v-if="showReclamation" class="contact__field contact__field--full">
          <label class="contact__label" for="c-recl">{{ t('auth.account.contact.detail') }} <span class="contact__req">*</span></label>
          <select id="c-recl" class="contact__input" :class="{ 'is-invalid': showErr('subReclamation') }" :name="TF.subReclamation" v-model="form.subReclamation" :aria-invalid="showErr('subReclamation')">
            <option value="" disabled>{{ t('auth.account.contact.choose') }}</option>
            <option v-for="o in SUB_RECLAMATION" :key="o.value" :value="o.value">{{ t('auth.account.contact.options.' + o.key) }}</option>
          </select>
          <p v-if="showErr('subReclamation')" class="contact__error">{{ errors.subReclamation }}</p>
        </div>

        <div v-if="showContract" class="contact__field contact__field--full">
          <label class="contact__label" for="c-contract">{{ t('auth.account.contact.contract') }} <span class="contact__req">*</span></label>
          <select id="c-contract" class="contact__input" :class="{ 'is-invalid': showErr('subContract') }" :name="TF.subContract" v-model="form.subContract" :aria-invalid="showErr('subContract')">
            <option value="" disabled>{{ t('auth.account.contact.choose') }}</option>
            <option v-for="o in SUB_CONTRACT" :key="o.value" :value="o.value">{{ t('auth.account.contact.options.' + o.key) }}</option>
          </select>
          <p v-if="showErr('subContract')" class="contact__error">{{ errors.subContract }}</p>
        </div>

        <div v-if="showContractHealth" class="contact__field contact__field--full">
          <label class="contact__label" for="c-health">{{ t('auth.account.contact.detail') }} <span class="contact__req">*</span></label>
          <select id="c-health" class="contact__input" :class="{ 'is-invalid': showErr('subContractHealth') }" :name="TF.subContractHealth" v-model="form.subContractHealth" :aria-invalid="showErr('subContractHealth')">
            <option value="" disabled>{{ t('auth.account.contact.choose') }}</option>
            <option v-for="o in SUB_CONTRACT_HEALTH" :key="o.value" :value="o.value">{{ t('auth.account.contact.options.' + o.key) }}</option>
          </select>
          <p v-if="showErr('subContractHealth')" class="contact__error">{{ errors.subContractHealth }}</p>
        </div>

        <div v-if="showContractAbroad" class="contact__field contact__field--full">
          <label class="contact__label" for="c-abroad">{{ t('auth.account.contact.detail') }} <span class="contact__req">*</span></label>
          <select id="c-abroad" class="contact__input" :class="{ 'is-invalid': showErr('subContractAbroad') }" :name="TF.subContractAbroad" v-model="form.subContractAbroad" :aria-invalid="showErr('subContractAbroad')">
            <option value="" disabled>{{ t('auth.account.contact.choose') }}</option>
            <option v-for="o in SUB_CONTRACT_ABROAD" :key="o.value" :value="o.value">{{ t('auth.account.contact.options.' + o.key) }}</option>
          </select>
          <p v-if="showErr('subContractAbroad')" class="contact__error">{{ errors.subContractAbroad }}</p>
        </div>

        <div class="contact__actions contact__field--full">
          <button type="submit" class="contact__submit">{{ t('auth.account.contact.submit') }}</button>
        </div>
      </form>
    </section>
  </AccountLayout>
</template>

<style scoped>
.contact {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  background: var(--color-white);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-sm);
  padding: clamp(1.25rem, 3.5vw, 2.25rem);
}

/* En-tête : pastille icône + titre + intro */
.contact__head { display: flex; align-items: flex-start; gap: 0.9rem; }
.contact__head-icon {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: var(--radius-lg);
  color: var(--primary);
  background: color-mix(in oklab, var(--primary) 12%, white);
}
.contact__title { font-size: var(--text-xl); font-weight: 700; color: var(--color-chocolate); }
.contact__intro { margin-top: 0.2rem; font-size: var(--text-sm); color: var(--text-muted); line-height: var(--leading-normal); max-width: 48rem; }

/* Grille 2 colonnes, pleine largeur */
.contact__form {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem 1.25rem;
}
.contact__field { display: flex; flex-direction: column; gap: 0.35rem; min-width: 0; }
.contact__field--full { grid-column: 1 / -1; }
.contact__label { font-size: var(--text-xs); font-weight: 600; color: var(--color-chocolate); }
.contact__req { color: var(--color-cherry); }
.contact__input {
  width: 100%;
  padding: 0.65rem 0.8rem;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  background: var(--color-white);
  font: inherit;
  font-size: var(--text-sm);
  color: var(--color-chocolate);
  transition: border-color var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out);
}
.contact__input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--focus-ring);
}
.contact__input.is-invalid {
  border-color: var(--color-cherry);
  box-shadow: 0 0 0 3px color-mix(in oklab, var(--color-cherry) 22%, transparent);
}
.contact__textarea { resize: vertical; min-height: 7rem; }
.contact__error { font-size: var(--text-xs); font-weight: 600; color: var(--color-cherry); }

.contact__actions { margin-top: 0.25rem; }
.contact__submit {
  padding: 0.75rem 1.6rem;
  border: 0;
  border-radius: var(--radius-pill);
  background: var(--color-chocolate);
  color: var(--color-white);
  font-size: var(--text-sm);
  font-weight: 600;
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease-out);
}
.contact__submit:hover { background: color-mix(in oklab, var(--color-chocolate) 85%, white); }

@media (max-width: 40rem) {
  .contact__form { grid-template-columns: 1fr; }
}
</style>
